/** Fuseau imposé pour la planification (Paris / Madrid). */
export const STATUS_SCHEDULE_TIMEZONE_LABEL = 'Europe/Paris (Paris, Madrid)';
const TZ = 'Europe/Paris';

/**
 * Affichage JJ/MM/AAAA et HH:mm à partir d'un ISO UTC stocké en base.
 */
export function utcIsoToParisDateAndTime(iso) {
  if (!iso || String(iso).trim() === '') return { date: '', time: '' };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { date: '', time: '' };
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: TZ,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(d);
  const get = (type) => parts.find((p) => p.type === type)?.value || '';
  return {
    date: `${get('day')}/${get('month')}/${get('year')}`,
    time: `${get('hour')}:${get('minute')}`,
  };
}

/**
 * Compose JJ/MM/AAAA + HH:mm (fuseau Paris) → instant UTC → ISO pour l'API.
 * @returns {string|null} ISO UTC ou null si saisie incomplète / invalide
 */
export function parisDateAndTimeToUtcIso(dateDmy, timeHm) {
  const d = String(dateDmy || '').trim();
  const t = String(timeHm || '').trim();
  if (!d || !t) return null;
  const dm = d.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  const tm = t.match(/^(\d{1,2}):(\d{2})$/);
  if (!dm || !tm) return null;
  const day = parseInt(dm[1], 10);
  const month = parseInt(dm[2], 10);
  const year = parseInt(dm[3], 10);
  let hour = parseInt(tm[1], 10);
  const minute = parseInt(tm[2], 10);
  if ([day, month, year, hour, minute].some((n) => Number.isNaN(n))) return null;
  if (month < 1 || month > 12 || day < 1 || day > 31 || hour > 23 || minute > 59) return null;

  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });

  const readParisClock = (instant) => {
    const parts = formatter.formatToParts(instant);
    const o = {};
    parts.forEach((p) => {
      if (p.type !== 'literal') o[p.type] = p.value;
    });
    return {
      y: parseInt(o.year, 10),
      mo: parseInt(o.month, 10),
      d: parseInt(o.day, 10),
      h: parseInt(o.hour, 10),
      mi: parseInt(o.minute, 10),
    };
  };

  const start = Date.UTC(year, month - 1, day, 0, 0, 0) - 12 * 3600 * 1000;
  const end = start + 50 * 3600 * 1000;
  for (let ms = start; ms <= end; ms += 60 * 1000) {
    const p = readParisClock(new Date(ms));
    if (p.y === year && p.mo === month && p.d === day && p.h === hour && p.mi === minute) {
      return new Date(ms).toISOString();
    }
  }
  return null;
}

export function parseDmyToDate(s) {
  const m = String(s || '').trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return undefined;
  const day = parseInt(m[1], 10);
  const month = parseInt(m[2], 10) - 1;
  const year = parseInt(m[3], 10);
  const d = new Date(year, month, day);
  if (d.getFullYear() !== year || d.getMonth() !== month || d.getDate() !== day) return undefined;
  return d;
}

export function formatDateToDmy(d) {
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

export function toTimeInputValue(t) {
  if (!t || !String(t).trim()) return '';
  const m = String(t).trim().match(/^(\d{1,2}):(\d{2})/);
  if (!m) return '';
  return `${String(Math.min(23, Math.max(0, parseInt(m[1], 10)))).padStart(2, '0')}:${String(Math.min(59, Math.max(0, parseInt(m[2], 10)))).padStart(2, '0')}`;
}

export function isAccountStatusScheduleComplete(fd) {
  const act =
    fd.status_program_activation === true &&
    String(fd.status_activation_date || '').trim() &&
    String(fd.status_activation_time || '').trim();
  const deact =
    fd.status_program_deactivation === true &&
    String(fd.status_deactivation_date || '').trim() &&
    String(fd.status_deactivation_time || '').trim();
  return !!(act || deact);
}

export function formatAccountStatusScheduleSummary(fd) {
  if (!isAccountStatusScheduleComplete(fd)) return '';
  if (fd.status_program_activation === true) {
    return `Activation programmée le ${fd.status_activation_date} à ${toTimeInputValue(fd.status_activation_time)}`;
  }
  return `Désactivation programmée le ${fd.status_deactivation_date} à ${toTimeInputValue(fd.status_deactivation_time)}`;
}

export function getDefaultAccountStatusFormFields() {
  return {
    is_active: true,
    status_program_activation: false,
    status_activation_date: '',
    status_activation_time: '',
    status_program_deactivation: false,
    status_deactivation_date: '',
    status_deactivation_time: '',
  };
}

export function applyAccountStatusFromApi(entity) {
  const actSched = utcIsoToParisDateAndTime(entity?.scheduled_activation_at);
  const deactSched = utcIsoToParisDateAndTime(entity?.scheduled_deactivation_at);
  return {
    is_active: entity?.is_active !== false,
    status_program_activation: Boolean(entity?.scheduled_activation_at && String(entity.scheduled_activation_at).trim()),
    status_activation_date: actSched.date,
    status_activation_time: actSched.time,
    status_program_deactivation: Boolean(entity?.scheduled_deactivation_at && String(entity.scheduled_deactivation_at).trim()),
    status_deactivation_date: deactSched.date,
    status_deactivation_time: deactSched.time,
  };
}

export function buildAccountStatusApiFields(fd) {
  return {
    is_active: fd.is_active !== false,
    scheduled_activation_at: fd.status_program_activation
      ? parisDateAndTimeToUtcIso(fd.status_activation_date, fd.status_activation_time)
      : null,
    scheduled_deactivation_at: fd.status_program_deactivation
      ? parisDateAndTimeToUtcIso(fd.status_deactivation_date, fd.status_deactivation_time)
      : null,
  };
}
